/*
 * The PDV prototype's design tokens. Every PDV style reads from here, so a colour
 * changes in one place and the admin screens and the rep's report flow stay alike.
 */

export const pdvColor = {
  textPrimary: '#333D47',
  textSecondary: '#5D7083',
  textHeading: '#20262D',
  textOnBrand: '#F8F9FA',

  surface: '#FFFFFF',
  surfacePage: '#F8F9FA',
  surfaceSubtle: '#EEF0F2',
  tooltip: '#20262D',

  border: '#EEF0F2',
  borderStrong: '#AFBAC6',

  brand: '#1E96FC',
  brandStrong: '#037AE2',
  brandHover: '#E8F4FF',
  brandTint: '#EBF5FF',
  brandSoft: '#D6EBFE',
  brandSofter: '#D2EAFE',

  success: '#23A566',
  successStrong: '#168A53',
  successTint: '#E6FAF0',
  warning: '#F08B0F',
  warningStrong: '#A35A00',
  warningTint: '#FCE8CF',
  danger: '#FA2E3B',
  dangerBright: '#FA2020',
  dangerTint: '#FED2D2'
} as const;

export const pdvRadius = { s: 8, m: 12, l: 20, pill: 999 } as const;

export const pdvShadow = {
  menu: '0 2px 4px #30363c0a, 0 16px 32px #30363c1a',
  modal: '0 4px 4px #30363c0a, 0 24px 48px -8px #30363c1f',
  banner: '0 2px 4px #30363c29'
} as const;

/** The 14/600 label most controls use. */
export const pdvLabelFont = {
  font: 'inherit',
  fontSize: 14,
  fontWeight: 600,
  lineHeight: '20px'
} as const;

/** Single-line text that ends in an ellipsis. */
export const pdvEllipsis = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
} as const;

/* Completeness colouring, shared so the tree, the tables and the bars agree. */
type Severity = 'good' | 'warn' | 'bad';

const severity = (pct: number): Severity => {
  if (pct >= 75) return 'good';
  if (pct >= 60) return 'warn';
  return 'bad';
};

const FG: Record<Severity, string> = {
  good: pdvColor.success,
  warn: pdvColor.warning,
  bad: pdvColor.danger
};
const BG: Record<Severity, string> = {
  good: pdvColor.successTint,
  warn: pdvColor.warningTint,
  bad: pdvColor.dangerTint
};
/* Tree dots use the brighter red; at 8px the other one reads as brown. */
const DOT: Record<Severity, string> = { ...FG, bad: pdvColor.dangerBright };

export const scoreColor = (pct: number) => FG[severity(pct)];
export const scoreBg = (pct: number) => BG[severity(pct)];
export const scoreDot = (pct: number) => DOT[severity(pct)];
/** The figure next to a bar: neutral once it passes, coloured while it lags. */
export const scoreText = (pct: number) =>
  severity(pct) === 'good' ? pdvColor.textPrimary : FG[severity(pct)];
